/**
 * Entities' histories at a glance (PRD-0075 R-C1, R-C2): one entity's summary for the History
 * card's header, and which entities of a list carry the "changed" badge. Both read
 * `GET /v1/cases/{id}/events/summary`; the keys sit under the history's, so whatever refreshes a
 * history refreshes them too.
 */
import { type UseQueryResult, useQueries, useQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import {
  caseEventSummariesApi,
  type EntitySummariesResponse,
  type EntitySummaryDto,
} from '@/services/api/case-events'
import { isRecentChange } from './summary-view'
import { entityHistoryKeys, retryUnlessRefused } from './useEntityHistory'

/** Uids per request: the list of a canvas fits in a few short URLs. */
const BATCH = 100
/** A badge may lag a change by a minute; a list does not re-read on every render. */
const BADGE_STALE_MS = 60_000

export const entitySummaryKeys = {
  forUids: (caseId: string, uids: readonly string[], asOf?: string) =>
    [...entityHistoryKeys.all(caseId), 'summary', uids.join(','), asOf ?? 'now'] as const,
}

/**
 * One entity's summary, as of now or as of `asOf` (*Spis k dátumu*); null for an entity the log
 * names nowhere, nothing read without a uid.
 */
export function useEntitySummary(caseId: string, uid: string | null, asOf?: string) {
  return useQuery<EntitySummaryDto | null>({
    queryKey: entitySummaryKeys.forUids(caseId, uid ? [uid] : [], asOf),
    queryFn: async ({ signal }) => {
      const response = await caseEventSummariesApi.summaries(caseId, [uid as string], {
        asOf,
        signal,
      })
      return response.summaries[0] ?? null
    },
    enabled: Boolean(caseId && uid),
    retry: retryUnlessRefused,
  })
}

/** `a,b,c` (distinct, sorted) in requests of at most `BATCH` uids. */
function batchesOf(key: string): string[][] {
  const uids = key ? key.split(',') : []
  const batches: string[][] = []
  for (let start = 0; start < uids.length; start += BATCH) {
    batches.push(uids.slice(start, start + BATCH))
  }
  return batches
}

/**
 * The uids of `uids` with a merge or property change younger than 30 days (R-C2). Empty while
 * the reads are in flight or failed: a missing badge never blocks a list.
 */
export function useChangedEntities(caseId: string, uids: readonly string[]): ReadonlySet<string> {
  // The batches follow the distinct uids, not the identity of the array a list renders.
  const key = [...new Set(uids)].sort().join(',')
  const batches = useMemo(() => batchesOf(key), [key])
  const combine = useCallback((results: UseQueryResult<EntitySummariesResponse>[]) => {
    const now = new Date()
    const changed = new Set<string>()
    for (const result of results) {
      for (const summary of result.data?.summaries ?? []) {
        if (isRecentChange(summary.last_change_at, now)) changed.add(summary.uid)
      }
    }
    return changed
  }, [])
  return useQueries({
    queries: batches.map(batch => ({
      queryKey: entitySummaryKeys.forUids(caseId, batch),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        caseEventSummariesApi.summaries(caseId, batch, { signal }),
      enabled: Boolean(caseId),
      staleTime: BADGE_STALE_MS,
      retry: retryUnlessRefused,
    })),
    combine,
  })
}
